import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66605 epic (Testmo folder 93168 part 2 of 9 — Style Name / Style Number
 * text filters and the partial-match rename). Real, persisted `stylesData` rows — NOT
 * `apiTransform` overrides — so the epic's specs exercise the genuine LIKE-partial-match path in
 * `styles.ts`'s `applyFilters` (an injected/overridden row would bypass that handler entirely and
 * make any result set look correct regardless of the filter logic under test).
 *
 * ids 97700+ reserved for this epic (no other `*FixtureData.ts` file in this range at time of
 * writing — checked empirically against every existing fixture id).
 *
 * Row groups, and which stories they exist for:
 *  - "Summer" / "Summer Dress" / "Winter Coat" — Style Name partial-vs-exact differentiation
 *    (754912): partial match must return BOTH "Summer" rows (the old exact-IN semantics would
 *    only return the literal "Summer" row), and must never return the "Winter Coat" decoy.
 *  - "SS26-001" / "SS26-002" / "AW25-777" — Style Number partial-vs-exact differentiation
 *    (754913) and the hyphenated-number case (788295/DL-66616): a hyphen in the query and the
 *    stored value must not defeat a plain substring match.
 *  - "Alpha Combo Style" / "Alpha Combo Extra" / "Solo Style" (customId "ALPHA-100-TWO" contains
 *    "ALPHA-100" as a genuine substring) — Name+Number AND composition (788289/DL-66610): the
 *    Name-only and Number-only result sets each have 2 members, but only ONE row satisfies both,
 *    so AND vs OR is falsifiable without pinning an exact unrelated row count.
 *  - "O'Brien Summer" / "100% Cotton Shirt" / "Style_Underscore Edition" / "Back\Slash Jacket" /
 *    "<script>Alert</script> Tee" — the security-flavoured metacharacter case (788294/DL-66615):
 *    apostrophe, and the SQL LIKE wildcards `%`/`_`, a backslash, and a markup string, each as a
 *    genuine substring of a real seeded name.
 *  - `TEXT_FILTER_LONG_NAME` (300+ chars) — the long-value no-500 case (788303/DL-66617).
 */

let nextId = 97700;
const id = (): number => nextId++;

type Overrides = Partial<StyleRow> & { name: string; customId: string };

const baseRow = (overrides: Overrides): StyleRow => ({
  id: id(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-03-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

/** >255 chars — a genuine style name long enough to prove the mock/table never 500s or truncates
 * a legitimately long stored value when it's substring-matched (788303/DL-66617). */
export const TEXT_FILTER_LONG_NAME = `Overlong Style Name Probe ${'Z'.repeat(240)} EndMarker`;

export const buildTextFilterFixtureStyles = (): StyleRow[] => [
  baseRow({ name: 'Summer', customId: 'TXTF-SUM-01' }),
  baseRow({ name: 'Summer Dress', customId: 'TXTF-SUM-02' }),
  baseRow({ name: 'Winter Coat', customId: 'TXTF-WIN-01' }),

  baseRow({ name: 'Hyphen Number Style A', customId: 'SS26-001' }),
  baseRow({ name: 'Hyphen Number Style B', customId: 'SS26-002' }),
  baseRow({ name: 'Hyphen Number Decoy', customId: 'AW25-777' }),

  baseRow({ name: 'Alpha Combo Style', customId: 'ALPHA-100' }),
  baseRow({ name: 'Alpha Combo Extra', customId: 'BETA-200' }),
  baseRow({ name: 'Solo Style', customId: 'ALPHA-100-TWO' }),

  baseRow({ name: "O'Brien Summer", customId: 'TXTF-OBRIEN' }),
  baseRow({ name: '100% Cotton Shirt', customId: 'TXTF-PERCENT' }),
  baseRow({ name: 'Style_Underscore Edition', customId: 'TXTF-UNDERSCORE' }),
  baseRow({ name: 'Back\\Slash Jacket', customId: 'TXTF-BACKSLASH' }),
  baseRow({ name: '<script>Alert</script> Tee', customId: 'TXTF-SCRIPT' }),

  baseRow({ name: TEXT_FILTER_LONG_NAME, customId: 'TXTF-LONGNAME' }),
];
